import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BkPage, Card, SmallBtn, fmtMoney, fmtTime } from "@/components/bookkeeping/BkPage";

const FILTERS = [["all", "All"], ["ai_category", "AI suggestions"], ["needs_category", "Needs a category"], ["expected_match", "Payment matches"], ["intercompany", "Intercompany"]];
const QUICK = 0.9;
const PREFILL = 0.5;
const input = "rounded-lg border border-[#333] bg-[#0F0F0F] px-2 py-1.5 text-xs text-white";

const aiProp = (t) => t.proposals.find((m) => m.match_type === "category" && m.source === "ai");
const selectable = (t, draft) => (t.review_reason === "expected_match" ? t.proposals.length === 1 : (t.review_reason === "ai_category" || t.review_reason === "needs_category") && !!draft.acct);

const CategorySelect = ({ cats, value, onChange, testId }) => (
  <select data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${input} max-w-[240px]`}>
    <option value="">Pick a category…</option>
    {cats.map((c) => <option key={c.qbo_id} value={c.qbo_id}>{c.name}</option>)}
  </select>
);

const CategoryEditor = ({ t, cats, draft, setDraft, act }) => {
  const ai = aiProp(t);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2">
      {ai?.qbo_account_id && (
        <span className="w-full text-xs text-neutral-400" data-testid={`bk-ai-suggestion-${t.id}`}>
          <b className="text-[#4CCC06]">AI {Math.round(ai.confidence * 100)}%</b> suggests <b className="text-white">{ai.account_name}</b>
          {ai.vendor_name && <> · {ai.vendor_name}</>}<span className="text-neutral-500"> · {ai.reasoning}</span>
          {ai.confidence < PREFILL && <span className="block text-[#F5B700]" data-testid={`bk-ai-low-${t.id}`}>Low confidence, so it isn't pre-selected. Pick the category yourself.</span>}
        </span>
      )}
      {!ai?.qbo_account_id && <span className="w-full text-xs text-[#F5B700]">{ai?.reasoning || "Pick a category for this transaction"}</span>}
      <CategorySelect cats={cats} value={draft.acct} onChange={(v) => setDraft({ acct: v })} testId={`bk-cat-select-${t.id}`} />
      <input data-testid={`bk-vendor-${t.id}`} value={draft.vendor ?? ""} onChange={(e) => setDraft({ vendor: e.target.value })} placeholder="Vendor / payee" className={`${input} w-40`} />
      <label className="flex items-center gap-1 text-xs text-neutral-300">
        <input type="checkbox" data-testid={`bk-remember-${t.id}`} checked={!!draft.remember} onChange={(e) => setDraft({ remember: e.target.checked })} />Always do this (save rule)
      </label>
      <span className="flex-1" />
      <SmallBtn primary disabled={!draft.acct} onClick={() => act("approve")} data-testid={`bk-cat-approve-${t.id}`}>Approve</SmallBtn>
      {ai && <SmallBtn onClick={() => act("reject", ai.id)} data-testid={`bk-cat-reject-${t.id}`}>Not this</SmallBtn>}
      <SmallBtn onClick={() => act("exclude")} data-testid={`bk-exclude-${t.id}`}>Exclude</SmallBtn>
    </div>
  );
};

const PaymentProposals = ({ t, act }) => t.proposals.map((m) => (
  <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#262626] bg-[#0F0F0F] px-3 py-2 text-xs">
    <span className="min-w-0 flex-1 text-neutral-300">
      {m.match_type === "intercompany_candidate" ? <b className="text-[#F5B700]">Possible intercompany transfer (handled in a later phase)</b>
        : <>Payment for <b className="text-white">{m.expected?.label}</b> · {m.expected?.counterparty_name} · {fmtMoney(m.amount)}</>}
      <span className="block text-neutral-500">{Math.round(m.confidence * 100)}% · {m.reasoning}</span>
    </span>
    {m.match_type !== "intercompany_candidate" && <SmallBtn primary onClick={() => act("payment", m.id)} data-testid={`bk-approve-${m.id}`}>Approve</SmallBtn>}
    <SmallBtn onClick={() => act("reject", m.id)} data-testid={`bk-reject-${m.id}`}>Not this</SmallBtn>
  </div>
));

const QueueRow = ({ t, cats, bankName, unlinked, checked, onCheck, draft, setDraft, act }) => (
  <div className="space-y-2 py-3" data-testid={`bk-review-${t.id}`}>
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <input type="checkbox" disabled={!selectable(t, draft)} checked={checked && selectable(t, draft)} onChange={(e) => onCheck(e.target.checked)} data-testid={`bk-select-${t.id}`} className="h-4 w-4 accent-[#4CCC06] disabled:opacity-30" title={selectable(t, draft) ? "" : "Pick a category first"} />
      <span className="w-24 text-neutral-400">{t.posted_date}</span>
      <span className="min-w-0 flex-1 font-bold text-white">{t.merchant_name || t.description}<span className="block text-xs font-normal text-neutral-500">{bankName}{unlinked && <span className="ml-2 font-bold text-[#F5B700]" data-testid={`bk-row-unlinked-${t.id}`}>not linked to QuickBooks</span>}</span></span>
      <span className="font-extrabold" style={{ color: t.amount >= 0 ? "#4CCC06" : "#fff" }}>{fmtMoney(t.amount)}</span>
    </div>
    {t.review_reason === "ai_category" || t.review_reason === "needs_category"
      ? <CategoryEditor t={t} cats={cats[t.amount < 0 ? "out" : "in"] || []} draft={draft} setDraft={setDraft} act={act} />
      : <PaymentProposals t={t} act={act} />}
  </div>
);

const DoneList = ({ rows, undo }) => (
  <Card title={`CATEGORIZED & EXCLUDED · ${rows.length}`} testId="bk-categorized-list">
    {rows.length === 0 ? <p className="text-sm text-neutral-500">Nothing categorized yet.</p> : (
      <div className="max-h-[380px] divide-y divide-[#1f1f1f] overflow-y-auto">
        {rows.map((m) => (
          <div key={m.id} className="flex flex-wrap items-center gap-3 py-2 text-xs" data-testid={`bk-cat-done-${m.id}`}>
            <span className="w-24 text-neutral-400">{m.txn?.posted_date}</span>
            <span className="min-w-0 flex-1 text-white">{m.txn?.merchant_name || m.txn?.description}
              <span className="block text-neutral-500">{m.match_type === "excluded" ? "Excluded" : `${m.account_name}${m.vendor_name ? ` · ${m.vendor_name}` : ""}`} · {m.source === "rule" ? "rule" : m.source === "ai" ? "AI, approved" : "manual"} · {m.reasoning}</span>
            </span>
            <span className="font-bold text-white">{fmtMoney(m.txn?.amount)}</span>
            <span className="w-40 text-neutral-400" data-testid={`bk-cat-status-${m.id}`}>
              {m.match_type === "excluded" ? "Not posted" : m.qbo_txn_id ? `QBO ${m.qbo_txn_type} #${m.qbo_txn_id}` : m.post_error ? <span className="text-[#FF6B6B]" title={m.post_error}>Post error: {m.post_error.slice(0, 40)}</span> : "Waiting to post"}
              <span className="block">{m.posted_to_qbo_at ? fmtTime(m.posted_to_qbo_at) : ""}</span>
            </span>
            <SmallBtn onClick={() => undo(m.id)} data-testid={`bk-cat-undo-${m.id}`}>Undo</SmallBtn>
          </div>
        ))}
      </div>
    )}
  </Card>
);

const useQueue = (entityId) => {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    const q = `entity_id=${entityId}`;
    const [review, cats, done, conn] = await Promise.all([api(`/bookkeeping/review?${q}`), api(`/bookkeeping/categories?${q}`), api(`/bookkeeping/categorized?${q}`), api(`/bookkeeping/connections?${q}`)]);
    setD({ review, cats, done, accounts: conn.bank_accounts, banks: Object.fromEntries(conn.bank_accounts.map((a) => [a.id, `${a.name} ••${a.mask || ""}`])) });
  }, [entityId]);
  useEffect(() => { setD(null); load().catch((e) => toast.error(e.message)); }, [load]);
  return [d, load];
};

const UnlinkedBanner = ({ accounts, review }) => {
  const off = accounts.map((a) => ({ ...a, n: review.filter((t) => t.bank_account_id === a.id).length })).filter((a) => !a.qbo_account_id && a.n > 0);
  if (!off.length) return null;
  return (
    <div className="rounded-xl border border-[#F5B700] bg-[#1A1505] p-4 text-sm text-neutral-200" data-testid="bk-unlinked-banner">
      <b className="text-[#F5B700]">Link these accounts to QuickBooks so approvals can post:</b>
      <ul className="mt-1 space-y-0.5">
        {off.map((a) => <li key={a.id} data-testid={`bk-unlinked-${a.id}`}>{a.name} ••{a.mask} <span className="text-neutral-400">· {a.n} waiting item(s) from it will stop at “Post error” until it's linked</span></li>)}
      </ul>
      <Link to="/bookkeeping/qbo-mapping" className="mt-2 inline-block text-xs font-bold text-[#4CCC06]" data-testid="bk-unlinked-fix">Link them on the QuickBooks mapping tab →</Link>
    </div>
  );
};

const Body = ({ entity }) => {
  const [d, load] = useQueue(entity.id);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel({}); setDrafts({}); }, [entity.id]);
  const draftOf = useCallback((t) => { const ai = aiProp(t); const pre = ai && ai.confidence >= PREFILL; return { acct: pre ? ai.qbo_account_id || "" : "", vendor: ai?.vendor_name || "", remember: false, ...drafts[t.id] }; }, [drafts]);
  const rows = useMemo(() => (d?.review || []).filter((t) => filter === "all" || t.review_reason === filter), [d, filter]);
  const later = (msg) => { toast.success(msg); setTimeout(() => load(), 4000); load(); };
  const itemFor = (t) => {
    if (t.review_reason === "expected_match") return { txn_id: t.id, match_id: t.proposals[0]?.id };
    const dr = draftOf(t); const ai = aiProp(t);
    return { txn_id: t.id, match_id: ai?.id, qbo_account_id: dr.acct || null, vendor_name: dr.vendor, remember: dr.remember };
  };
  const approve = async (items) => {
    setBusy(true);
    try {
      const r = await api("/bookkeeping/review/approve", { method: "POST", body: { items } });
      r.failed ? toast.error(`${r.approved} approved, ${r.failed} need attention: ${r.results.find((x) => !x.ok).error}`) : later(`${r.approved} approved${r.rule_applied ? ` · rule applied to ${r.rule_applied} more` : ""}; posting to QuickBooks`);
      setSel({}); load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const act = (t) => async (kind, mid) => {
    try {
      if (kind === "approve" || kind === "payment") return approve([kind === "payment" ? { txn_id: t.id, match_id: mid } : itemFor(t)]);
      if (kind === "reject") await api(`/bookkeeping/matches/${mid}/undo`, { method: "POST" });
      if (kind === "exclude") await api(`/bookkeeping/transactions/${t.id}/exclude`, { method: "POST" });
      later(kind === "exclude" ? "Excluded from the books" : "Suggestion removed");
    } catch (e) { toast.error(e.message); }
  };
  const undo = async (mid) => { try { await api(`/bookkeeping/matches/${mid}/undo`, { method: "POST" }); later("Undone; back in the queue"); } catch (e) { toast.error(e.message); } };
  if (!d) return null;
  const picked = rows.filter((t) => sel[t.id] && selectable(t, draftOf(t)));
  const quick = () => setSel(Object.fromEntries(rows.filter((t) => (aiProp(t)?.confidence || 0) >= QUICK && aiProp(t)?.qbo_account_id).map((t) => [t.id, true])));
  return (
    <>
      <UnlinkedBanner accounts={d.accounts} review={d.review} />
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map(([k, label]) => {
          const n = k === "all" ? d.review.length : d.review.filter((t) => t.review_reason === k).length;
          return <button key={k} onClick={() => setFilter(k)} data-testid={`bk-filter-${k}`} className="rounded-full border px-3 py-1 text-xs font-bold" style={filter === k ? { background: "#4CCC06", color: "#000", borderColor: "#4CCC06" } : { color: "#D4D4D4", borderColor: "#333" }}>{label} · {n}</button>;
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SmallBtn onClick={quick} data-testid="bk-select-ai-90">Select AI suggestions ≥ 90%</SmallBtn>
        <SmallBtn onClick={() => setSel({})} data-testid="bk-select-none">Clear</SmallBtn>
        <SmallBtn primary disabled={!picked.length || busy} onClick={() => approve(picked.map(itemFor))} data-testid="bk-bulk-approve">Approve selected ({picked.length})</SmallBtn>
        <span className="flex-1" />
        <SmallBtn onClick={async () => { try { await api(`/bookkeeping/categorize/run?entity_id=${entity.id}`, { method: "POST" }); toast.success("Applying rules and asking AI; refreshing shortly"); setTimeout(() => load(), 15000); } catch (e) { toast.error(e.message); } }} data-testid="bk-run-categorize">Run rules & AI now</SmallBtn>
        <SmallBtn onClick={() => { api(`/bookkeeping/expenses/post?entity_id=${entity.id}`, { method: "POST" }).then(() => later("Posting approved items to QuickBooks")).catch((e) => toast.error(e.message)); }} data-testid="bk-post-expenses">Post approved now</SmallBtn>
        <SmallBtn onClick={load} data-testid="bk-review-refresh">Refresh</SmallBtn>
      </div>
      <Card title={`REVIEW QUEUE · ${rows.length}`} testId="bk-review-queue">
        {rows.length === 0 ? <p className="text-sm text-neutral-500" data-testid="bk-review-empty">Nothing waiting for review.</p> : (
          <div className="divide-y divide-[#262626]">
            {rows.map((t) => (
              <QueueRow key={t.id} t={t} cats={d.cats} bankName={d.banks[t.bank_account_id]} unlinked={!d.accounts.find((a) => a.id === t.bank_account_id)?.qbo_account_id} checked={!!sel[t.id]} onCheck={(v) => setSel((s) => ({ ...s, [t.id]: v }))}
                draft={draftOf(t)} setDraft={(p) => setDrafts((x) => ({ ...x, [t.id]: { ...draftOf(t), ...p } }))} act={act(t)} />
            ))}
          </div>
        )}
      </Card>
      <DoneList rows={d.done} undo={undo} />
    </>
  );
};

export default function Review() {
  return <BkPage title="Review queue" testId="bookkeeping-review">{({ entity }) => <Body entity={entity} />}</BkPage>;
}
